import { useState } from "react";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { AirSavedCardModel } from "entities/AirSavedCard";
import { isSeaSavedShipmentModel } from "entities/SeaSavedCard";
import { SeaSavedCardModel } from "entities/SeaSavedCard";
import { createSeaUpdateCardHook } from "./useSeaCardUpdateAction";

type UseSeaUpdateAllCardsActionParams = {
  filteredCards: (SeaSavedCardModel | AirSavedCardModel)[];
  seaSavedCards: SeaSavedCardModel[];
  cardsByTags: (SeaSavedCardModel | AirSavedCardModel)[];
  closeUpdateAllCardsModal: () => void;
  updateFilteredCards: (response: SeaSavedCardModel) => {
    type: string;
    payload: SeaSavedCardModel;
  };
};

type UseSeaUpdateAllCardsActionReturnType = {
  updateAllSeaCards: () => void;
  canCloseUpdateAllSeaCardsModal: boolean;
  seaProgress: {
    total: number;
    current: number;
  };
  seaResults: { reference: string | null; isSuccess: boolean }[];
  seaResetState: () => void;
};

export const useSeaUpdateAllCardsAction = ({
  filteredCards,
  seaSavedCards,
  cardsByTags,
  updateFilteredCards,
}: UseSeaUpdateAllCardsActionParams): UseSeaUpdateAllCardsActionReturnType => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState<UseSeaUpdateAllCardsActionReturnType["seaProgress"]>({
    total: 0,
    current: 0,
  });
  const [results, setResults] = useState<{ reference: string | null; isSuccess: boolean }[]>([]);
  const { isCurrentViewFilteredCards, isCurrentViewMain, isCurrentViewCardsByTag } = usePanelViews();
  const { updateAllCards } = createSeaUpdateCardHook({
    updateFilteredCards,
  })();

  const resetState = () => {
    setResults([]);
    setProgress({ total: 0, current: 0 });
    setIsLoading(false);
  };

  const updateCards = async (cards: SeaSavedCardModel[]) => {
    const cardGroups = cards.reduce<SeaSavedCardModel[][]>((groups, card, index) => {
      const groupIndex = Math.floor(index / 5);
      if (!groups[groupIndex]) {
        groups[groupIndex] = [];
      }
      groups[groupIndex].push(card);
      return groups;
    }, []);

    setIsLoading(true);
    setProgress({ total: cards.length, current: 0 });
    for (const group of cardGroups) {
      const { results, canUpdateMore } = await updateAllCards(group);
      setResults((prev) => [...prev, ...results]);
      setProgress((prev) => ({ ...prev, current: prev.current + group.length }));
      if (!canUpdateMore) {
        break;
      }
    }
    setIsLoading(false);
  };

  const updateAllSeaCards = async () => {
    try {
      if (isCurrentViewFilteredCards) {
        await updateCards(filteredCards.filter((card) => isSeaSavedShipmentModel(card)));
      }
      if (isCurrentViewMain) {
        await updateCards(seaSavedCards);
      }
      if (isCurrentViewCardsByTag) {
        await updateCards(cardsByTags.filter((card) => isSeaSavedShipmentModel(card)));
      }
    } catch (_) {}
  };

  return {
    updateAllSeaCards,
    canCloseUpdateAllSeaCardsModal: !isLoading,
    seaProgress: progress,
    seaResults: results,
    seaResetState: resetState,
  };
};
